// Google Identity Services (GIS) – ugyanaz a minta, mint a GLC-RAG frontend `googleAuth.ts`

let scriptPromise: Promise<void> | null = null;

export type GoogleIdTokenResponse = {
    credential: string;
    select_by?: string;
};

declare global {
    interface Window {
        google?: any;
    }
}

export function loadGoogleIdentityScript(): Promise<void> {
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise<void>((resolve, reject) => {
        if (window.google?.accounts?.id) {
            resolve();
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Identity script'));
        document.head.appendChild(script);
    });

    return scriptPromise;
}

export async function renderGoogleButton(params: {
    element: HTMLElement;
    clientId: string;
    onCredential: (idToken: string) => void;
    onError?: (err: Error) => void;
    context?: 'signin' | 'signup';
    text?: 'continue_with' | 'signin_with' | 'signup_with';
}): Promise<void> {
    const { element, clientId, onCredential, onError, context, text } = params;
    try {
        await loadGoogleIdentityScript();
        const google = window.google;
        if (!google?.accounts?.id) throw new Error('Google Identity Services not available');

        element.innerHTML = '';

        google.accounts.id.initialize({
            client_id: clientId,
            context: context || 'signin',
            ux_mode: 'popup',
            callback: (resp: GoogleIdTokenResponse) => {
                if (!resp?.credential) {
                    onError?.(new Error('Missing credential from Google'));
                    return;
                }
                onCredential(resp.credential);
            },
        });

        google.accounts.id.renderButton(element, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            width: element.clientWidth ? String(element.clientWidth) : undefined,
            text: text || 'continue_with',
        });
    } catch (e: unknown) {
        onError?.(e instanceof Error ? e : new Error(String(e)));
    }
}
